import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    question: 'How does Sortio organize MLS listing photos by property address?',
    answer:
      "Sortio reads the visual content and EXIF metadata of each photo, then matches it to the property folder you already use. DSC_0123.JPG and DJI_0245.JPG land in a folder like 1127 S Cornell Terrace, renamed with the property address so the MLS upload and your archive both stay clean. You can run the AI Rule Builder once to teach Sortio your address format, and every future batch routes the same way.",
  },
  {
    question: 'Can Sortio rename files like DSC_0001.JPG to something useful for listings?',
    answer:
      'Yes. Sortio uses content-rename to turn camera filenames into descriptive ones, for example 1127 S Cornell Terrace - Kitchen - 01.jpg or 1127 S Cornell Terrace - Drone Front Elevation.jpg. You preview the new names before any file moves, and the original can be restored from sort history with one click.',
  },
  {
    question: 'Does Sortio handle drone shoots from DJI and mirrorless cameras like the Sony ILCE-7M4?',
    answer:
      "Drone files (DJI_*.JPG, DJI_*.DNG) and mirrorless raw or JPEG output sort the same way as any other listing photo. Sortio reads the embedded EXIF (camera model, capture date, GPS when present) and pairs that with the property you're shooting that day. Mixed Sony ILCE-7M4 interior shots and DJI drone exteriors land in the same property folder without you sorting by hand.",
  },
  {
    question: 'How does Sortio file ALTA settlement statements and seller net sheets at closing?',
    answer:
      'Closing PDFs are read locally to extract the property address, buyer and seller names, and closing date. A file named ALTA-Combined-Settlement-Statement.pdf gets renamed to something like ALTA Settlement Statement - 1127 S Cornell Terrace - 2026-04-12.pdf and routed under that property folder. Seller net sheets follow the same pattern, so when your CPA asks at tax time you find the document in seconds.',
  },
  {
    question: 'Does Sortio integrate with dotloop, zipForm, or Sky Slope?',
    answer:
      "Sortio does not have a direct API integration with dotloop, zipForm, or Sky Slope today. What it does instead is work on any folder you can see in File Explorer or Finder, including the local sync folder those tools write to, or the Downloads folder where you save signed copies. Many agents use Sortio to file the exports and signed PDFs that come out of those platforms.",
  },
  {
    question: 'Will Sortio work with my OneDrive, Dropbox, Box, or iCloud Drive?',
    answer:
      "Yes. Sortio is filesystem-transparent, so any folder visible in File Explorer or Finder is fair game, including OneDrive\\Desktop, Dropbox, Box, Google Drive Desktop, and iCloud Drive. You can point it at OneDrive\\Desktop\\1127 S Cornell Terrace or your top-level Listings folder and it routes new files into the correct property as they arrive.",
  },
  {
    question: 'I get hundreds of files per closing. Can Sortio handle that volume?',
    answer:
      'Pro can run up to 5,000 files per AI sort, which covers a typical 200 to 600 file closing comfortably. For very large archives (a multi-year backlog of listings, for example), we recommend setting up the AI Rule Builder once, then running the rule locally. Rule-based sorts are much faster than AI sorts on big batches and they never touch your AI allowance.',
  },
  {
    question: 'How does Sortio separate transaction documents from my tax archive?',
    answer:
      "Sortio uses Spaces to keep distinct contexts apart. Your Per-Property Transactions Space routes files by property address. A separate Tax Archive Space routes 1099s, brokerage commission statements, and year-end summaries into folders by tax year. Both run on the same machine, but they don't bleed into each other, so a settlement statement never lands in the tax folder.",
  },
  {
    question: 'Can Sortio help with seller disclosures and state-specific forms?',
    answer:
      "Yes. Sortio recognizes named forms (for example LPT Realty Sellers Non-Occupancy Disclosure, FAR-BAR AS IS, Lead Based Paint Disclosure) and files them under the matching property with a clean name. If you work in multiple states, you can have separate Spaces per state license so a Florida disclosure never gets routed into a Texas property's folder.",
  },
  {
    question: 'I work both sides of a dual agency deal. How does Sortio keep buyer and seller files straight?',
    answer:
      "Sortio reads the role each document represents (buyer agency agreement, listing agreement, transaction broker disclosure) and you can configure the Rule Builder to file dual agency transactions into a single property folder with buyer/ and seller/ subfolders. The audit trail shows exactly which side each file was tagged as, so you have a clean record if there's ever a question.",
  },
  {
    question: 'Does Sortio organize files by tax year for end-of-year reporting?',
    answer:
      'Yes. The Tax Archive Space routes brokerage 1099s, commission statements, mileage logs, and home office receipts into folders by tax year. Closed transaction documents stay in their property folders for the deal record, while the financial documents your CPA needs land in a separate, dated structure. At year end you hand your CPA one folder, not 40.',
  },
  {
    question: "Is Sortio safe for client financial documents and signed disclosures?",
    answer:
      "Sortio reads settlement statements and disclosures on your Mac or PC locally. In Offline mode (a local LLM like Ollama) or with BYOK (bring your own API key), file contents stay on your machine, so even the inference happens under your control. By default Sortio uses our cloud (OpenAI) to plan the sort and sends filenames and folder paths; file contents are sent only if you enable a content-based feature like renaming from document text. Choose a local LLM and a buyer's social or a seller's bank info never crosses the network at all. Data is encrypted in transit (TLS 1.2+) and at rest.",
  },
  {
    question: 'Can I run Sortio on my side e-commerce or investment-property files too?',
    answer:
      'Yes. Many of our agents also run a small side venture, an e-commerce store, a rental portfolio, a consulting practice. You can create a separate Space for each context, so listing photos, Shopify exports, and rental ledgers each get their own rules and never get mixed. One Sortio install, multiple cleanly separated workflows.',
  },
  {
    question: 'How do I get started if my Desktop already has thousands of files on it?',
    answer:
      'Start by pointing Sortio at your Desktop or your transaction folder, then run an AI sort on a single recent closing (say 200 files) to see the routing in action. Once you like the result, open the AI Rule Builder to capture that routing as a reusable rule, and apply it to the rest of your backlog locally. Most solo agents go from a 3,000 file Desktop to clean per-property folders in an evening.',
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => {
  return (
    <div className="bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between text-left p-6 hover:bg-apple-800/60 transition-colors"
        aria-expanded={isOpen}
      >
        <h3 className="text-base sm:text-lg font-medium text-apple-50 pr-6 leading-snug">
          {faq.question}
        </h3>
        <ChevronDown
          className={`text-sortio-primary flex-shrink-0 mt-1 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          size={20}
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-6 -mt-1">
          <p className="text-sm text-apple-200 leading-relaxed">{faq.answer}</p>
        </div>
      )}
    </div>
  );
};

const RealtorsFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section id="faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="text-center mb-14">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sortio-primary/15 border border-sortio-primary/30 mb-4">
          <HelpCircle className="text-sortio-primary" size={20} />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          Real estate agent FAQ
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Answers to the questions we hear from solo residential agents, transaction coordinators,
          and small brokerages about filing MLS photos, settlement statements, and per-property
          transaction documents.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-3">
        {FAQS.map((faq, i) => (
          <FAQItem
            key={i}
            faq={faq}
            isOpen={openIndex === i}
            onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
          />
        ))}
      </div>
    </section>
  );
};

export default RealtorsFAQ;
